import { useState } from 'react';
import { useMail } from '../../context/MailContext';
import './Settings.css';

export default function SettingsPanel() {
  const { activeFolder } = useMail();
  if (activeFolder === 'autoresponder') return <AutoResponderSettings />;
  if (activeFolder === 'signature') return <SignatureSettings />;
  if (activeFolder === 'settings') return <GeneralSettings />;
  return null;
}

function Toggle({ on, onToggle }) {
  return (
    <button
      className={`toggle ${on ? 'on' : ''}`}
      onClick={onToggle}
      aria-label={on ? 'Disable' : 'Enable'}
    />
  );
}

function AutoResponderSettings() {
  const [rules, setRules] = useState([
    { id: 1, name: 'Out of Office', enabled: true, desc: 'Jun 10 – Jun 17', reply: "I'm on leave, back June 17. For urgent matters contact hr@ellertek.co.tz" },
    { id: 2, name: 'Weekend Auto-Reply', enabled: false, desc: 'Saturday & Sunday', reply: "Thank you for your email. I'll respond on Monday." },
    { id: 3, name: 'New Inquiry Reply', enabled: true, desc: 'Subject contains "inquiry"', reply: "Thank you for your inquiry. We'll respond within 24 hours." },
  ]);
  const [showAdd, setShowAdd] = useState(false);
  const [newRule, setNewRule] = useState({ name: '', desc: '', reply: '' });

  const toggle = (id) => setRules(r => r.map(x => x.id === id ? { ...x, enabled: !x.enabled } : x));
  const remove = (id) => setRules(r => r.filter(x => x.id !== id));
  const add = () => {
    if (!newRule.name) return;
    setRules(r => [...r, { ...newRule, id: Date.now(), enabled: true }]);
    setNewRule({ name: '', desc: '', reply: '' });
    setShowAdd(false);
  };

  return (
    <div className="settings-panel">
      <div className="settings-header">
        <h2 className="settings-title">🤖 Auto-Responders</h2>
        <p className="settings-desc">Automatically reply to incoming emails based on rules or schedule.</p>
      </div>

      <div className="settings-list">
        {rules.map(rule => (
          <div key={rule.id} className={`rule-card ${rule.enabled ? 'active' : ''}`}>
            <div className="rule-top">
              <div className="rule-info">
                <div className="rule-name">{rule.name}</div>
                <div className="rule-desc">{rule.desc}</div>
              </div>
              <div className="rule-actions">
                <Toggle on={rule.enabled} onToggle={() => toggle(rule.id)} />
                <button className="rule-del" onClick={() => remove(rule.id)} title="Delete">✕</button>
              </div>
            </div>
            <div className="rule-reply">"{rule.reply}"</div>
          </div>
        ))}
      </div>

      {showAdd ? (
        <div className="add-rule-form">
          <h3>New Auto-Responder Rule</h3>
          <input className="settings-input" placeholder="Rule name (e.g. Out of Office)"
            value={newRule.name} onChange={e => setNewRule(r => ({ ...r, name: e.target.value }))} />
          <input className="settings-input" placeholder="Condition (e.g. Jun 1 – Jun 7)"
            value={newRule.desc} onChange={e => setNewRule(r => ({ ...r, desc: e.target.value }))} />
          <textarea className="settings-textarea" placeholder="Auto-reply message…"
            value={newRule.reply} onChange={e => setNewRule(r => ({ ...r, reply: e.target.value }))} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-save" onClick={add}>Save Rule</button>
            <button className="btn-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="add-rule-btn" onClick={() => setShowAdd(true)}>
          + Add Auto-Responder Rule
        </button>
      )}
    </div>
  );
}

function SignatureSettings() {
  const [signatures, setSignatures] = useState([
    {
      id: 1, name: 'Default',
      content: `John Doe\nSoftware Engineer · Ellertek Ltd\njohn.doe@ellertek.co.tz | +255 700 000 000\nwww.ellertek.co.tz`,
      active: true,
    },
  ]);
  const [editing, setEditing] = useState(null);

  return (
    <div className="settings-panel">
      <div className="settings-header">
        <h2 className="settings-title">✍ Email Signatures</h2>
        <p className="settings-desc">Your signature is added automatically to new emails and replies.</p>
      </div>

      {signatures.map(sig => (
        <div key={sig.id} className={`sig-card ${sig.active ? 'active' : ''}`}>
          <div className="sig-header">
            <div>
              <div className="sig-name">{sig.name}</div>
              {sig.active && <span className="sig-badge">Default</span>}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="rule-btn" onClick={() => setEditing(editing === sig.id ? null : sig.id)}>
                {editing === sig.id ? 'Done' : '✏ Edit'}
              </button>
            </div>
          </div>
          {editing === sig.id ? (
            <textarea
              className="settings-textarea"
              value={sig.content}
              onChange={e => setSignatures(s => s.map(x => x.id === sig.id ? { ...x, content: e.target.value } : x))}
            />
          ) : (
            <pre className="sig-preview">{sig.content}</pre>
          )}
        </div>
      ))}

      <button className="add-rule-btn" onClick={() =>
        setSignatures(s => [...s, { id: Date.now(), name: 'New Signature', content: '', active: false }])
      }>
        + Add Signature
      </button>
    </div>
  );
}

function GeneralSettings() {
  const [prefs, setPrefs] = useState({
    readReceipts: true,
    undoWindow: '10',
    scheduling: true,
    desktopNotif: true,
    autoSaveDraft: true,
    replyAll: false,
  });

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  return (
    <div className="settings-panel">
      <div className="settings-header">
        <h2 className="settings-title">⚙ General Settings</h2>
        <p className="settings-desc">Manage your mail preferences and server connection.</p>
      </div>

      <div className="settings-section">
        <h3 className="section-label">Email behaviour</h3>
        <SettingRow
          label="Read receipts"
          desc="Notify you when recipients open your emails"
          control={<Toggle on={prefs.readReceipts} onToggle={() => toggle('readReceipts')} />}
        />
        <SettingRow
          label="Undo send window"
          desc="Time to recall an email after sending"
          control={
            <select className="settings-select"
              value={prefs.undoWindow}
              onChange={e => setPrefs(p => ({ ...p, undoWindow: e.target.value }))}>
              <option value="5">5 seconds</option>
              <option value="10">10 seconds</option>
              <option value="20">20 seconds</option>
              <option value="30">30 seconds</option>
            </select>
          }
        />
        <SettingRow
          label="Email scheduling"
          desc="Allow sending emails at a later date/time"
          control={<Toggle on={prefs.scheduling} onToggle={() => toggle('scheduling')} />}
        />
        <SettingRow
          label="Auto-save drafts"
          desc="Save draft every 30 seconds while composing"
          control={<Toggle on={prefs.autoSaveDraft} onToggle={() => toggle('autoSaveDraft')} />}
        />
        <SettingRow
          label="Desktop notifications"
          desc="Show browser notification on new email"
          control={<Toggle on={prefs.desktopNotif} onToggle={() => toggle('desktopNotif')} />}
        />
      </div>

      <div className="settings-section">
        <h3 className="section-label">Server connection (cPanel)</h3>
        <SettingRow
          label="IMAP Server"
          desc="ellertek.co.tz · Port 993 (SSL/TLS)"
          control={<span className="status-badge success">Connected</span>}
        />
        <SettingRow
          label="SMTP Server"
          desc="ellertek.co.tz · Port 465 (SSL)"
          control={<span className="status-badge success">Connected</span>}
        />
        <div className="cpanel-guide">
          <h4>cPanel setup guide</h4>
          <p>1. Log in to cPanel → Email Accounts → connect settings</p>
          <p>2. Use these credentials in your backend .env file:</p>
          <code>IMAP_HOST=ellertek.co.tz</code>
          <code>IMAP_PORT=993</code>
          <code>SMTP_HOST=ellertek.co.tz</code>
          <code>SMTP_PORT=465</code>
          <p>3. Credentials: full email + cPanel email password</p>
        </div>
      </div>

      <div className="settings-section">
        <h3 className="section-label">Admin</h3>
        <SettingRow
          label="Manage employee accounts"
          desc="Add, remove, and configure staff email accounts"
          control={<button className="rule-btn">Open Admin Panel →</button>}
        />
      </div>
    </div>
  );
}

function SettingRow({ label, desc, control }) {
  return (
    <div className="setting-row">
      <div className="setting-info">
        <div className="setting-label">{label}</div>
        {desc && <div className="setting-desc">{desc}</div>}
      </div>
      <div className="setting-control">{control}</div>
    </div>
  );
}
